import { push } from "connected-react-router";
import * as servicePaymentsMiddleware from "middleware/servicePayments";
import { getStateByCode, transactionStatus } from "pages/transactions/constants";
import { actions as notificationActions } from "reducers/notification";
import { types } from "reducers/servicePayments";
import { call, put, takeLatest } from "redux-saga/effects";
import { formatCompanyOptions, formatModeOptions } from "util/servicePayments";
import * as i18n from "util/i18n";

const sagas = [
    takeLatest(types.COMPANY_SEARCH_REQUEST, searchCompanySaga),
    takeLatest(types.PAYMENT_MODES_REQUEST, getPaymentModesSaga),
    takeLatest(types.SERVICE_BILLING_REQUEST, getServiceBillingSaga),
    takeLatest(types.PREPARE_PAYMENT_REQUEST, preparePaymentSaga),
    takeLatest(types.PAY_SERVICE_REQUEST, payServiceSaga),
    takeLatest(types.EXPIRATION_AGENDA_BILLS_REQUEST, getdebtSaga),
    takeLatest(types.SCHEDULED_BILL_REQUEST, getScheduledBillSaga),
    takeLatest(types.VOUCHER_REQUEST, rapipagoVoucher),
];

export default sagas;

const ticketStates = {
    COR023I: transactionStatus.PENDING,
    COR000I: transactionStatus.FINISHED,
    COR013I: transactionStatus.SCHEDULED,
};

function* searchCompanySaga({ searchString }) {
    try {
        const response = yield call(servicePaymentsMiddleware.searchCompany, { searchString });
        if (response.data.code === "COR000I") {
            yield put({
                type: types.COMPANY_SEARCH_SUCCESS,
                companies: formatCompanyOptions(response.data.data.companies),
            });
            if (response.data.data.companies.length === 0) {
                yield put(
                    notificationActions.showNotification(i18n.get("agenda.no.results"), "error", [
                        "newServicePaymentPage",
                    ]),
                );
            }
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.message, "error", ["newServicePaymentPage"]));
        yield put({
            type: types.COMPANY_SEARCH_FAILURE,
            message: error.data.message,
        });
    }
}

function* getPaymentModesSaga({ companyCode }) {
    try {
        const response = yield call(servicePaymentsMiddleware.searchPaymentModes, { companyCode });
        if (response.data.code === "COR000I") {
            yield put({
                type: types.PAYMENT_MODES_SUCCESS,
                companyCode,
                paymentModes: formatModeOptions(response.data.data.paymentModes),
            });
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.message, "error", ["newServicePaymentPage"]));
        yield put({
            type: types.PAYMENT_MODES_FAILURE,
            message: error.data.message,
        });
    }
}

function* getServiceBillingSaga({ invoiceParams }) {
    try {
        const response = yield call(servicePaymentsMiddleware.searchBilling, invoiceParams);
        if (response.data.code === "COR000I") {
            if (response.data.data.billing?.length === 0) {
                yield put(
                    notificationActions.showNotification(i18n.get("global.no.results"), "error", [
                        "newServicePaymentPage",
                    ]),
                );
            }
            yield put({
                type: types.SERVICE_BILLING_SUCCESS,
                billingList: response.data.data.billing,
            });
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.data.message, "error", ["newServicePaymentPage"]));
        yield put({
            type: types.SERVICE_BILLING_FAILURE,
            message: error.data.data.message,
        });
    }
}

function* preparePaymentSaga({ prepareData }) {
    const data = { ...prepareData, amount: prepareData.amount.quantity };
    try {
        const response = yield call(servicePaymentsMiddleware.billPrepare, data);
        if (response.data.code === "COR000I") {
            yield put({
                type: types.PREPARE_PAYMENT_SUCCESS,
                prepareData: {
                    ...prepareData,
                    operationId: response.data.data.operationId,
                    operationStatus: response.data.data.operationStatus,
                },
            });
            yield put(push("/servicePayments/summary"));
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.data.message, "error", ["newServicePaymentPage"]));
        yield put({
            type: types.PREPARE_PAYMENT_FAILURE,
            message: error.data.data.message,
        });
    }
}

function* payServiceSaga({ paymentData, formikBag, credentialType }) {
    try {
        const response = yield call(servicePaymentsMiddleware.payService, { ...paymentData, routeParam: "services" });
        const { isFinished, category } = getStateByCode(response.data?.code);
        const { isWarning, isError } = category;

        if (isWarning || isError) {
            const msj = response.data?.data[credentialType];
            const validateHour = response.data?.data?.VALIDATE_HOUR;
            const validateBalance = response.data?.data?.BALANCE;
            if (msj) {
                yield put(notificationActions.showNotification(msj, "error", ["sumaryServicePaymentPage"]));
                formikBag.setErrors({
                    credential: msj,
                });
                return;
            }
            if (validateHour) {
                yield put(notificationActions.showNotification(validateHour, "error", ["sumaryServicePaymentPage"]));
                return;
            }
            if (validateBalance) {
                yield put(
                    notificationActions.showNotification(i18n.get(validateBalance), "error", [
                        "sumaryServicePaymentPage",
                    ]),
                );
                return;
            }
            yield put(
                notificationActions.showNotification(response.data?.data?.message, "error", [
                    "sumaryServicePaymentPage",
                ]),
            );
            return;
        }
        yield put({
            type: types.PAY_SERVICE_SUCCESS,
            servicePaymentTicket: {
                status: ticketStates[response.data.code],
                ...response.data,
            },
        });
        if (isFinished) {
            yield put(
                notificationActions.showNotification(i18n.get("transfer.success"), "success", [
                    "servicePaymentTransactionPage",
                ]),
            );
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.message, "error", ["sumaryServicePaymentPage"]));
        yield put({
            type: types.PAY_SERVICE_FAILURE,
            message: error.data.message,
        });
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* getdebtSaga() {
    try {
        const response = yield call(servicePaymentsMiddleware.getDebts);
        const { billList = [] } = response.data.data;
        const debts = [];
        billList.forEach((element) => {
            element.bills.map((bill) => debts.push({ serviceId: element.serviceId, ...bill }));
        });
        if (response.data.code === "COR000I") {
            yield put({
                type: types.EXPIRATION_AGENDA_BILLS_SUCCESS,
                debts,
            });
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.message, "error", ["servicePaymentsPage"]));
        yield put({
            type: types.SERVICE_BILLING_FAILURE,
            message: error.data.message,
        });
    }
}

function* getScheduledBillSaga({ serviceId, userCode = "" }) {
    try {
        const response = yield call(servicePaymentsMiddleware.getScheduledBill, { serviceId, userCode });
        if (response.data.code === "COR000I") {
            yield put({
                type: types.SCHEDULED_BILL_SUCCESS,
                scheduledBill: response.data.data.debt,
            });
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.message, "error", ["expirationAgendaList"]));
        yield put({
            type: types.SCHEDULED_BILL_FAILURE,
            message: error.data.message,
        });
    }
}

function* rapipagoVoucher({ operationId }) {
    try {
        const response = yield call(servicePaymentsMiddleware.rapipagoVoucher, { operationId });
        if (response.data.code === "COR000I") {
            yield put({
                type: types.VOUCHER_SUCCESS,
                voucherData: response.data.data.voucherItems,
            });
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.data.message, "error", ["servicePaymentTransactionPage"]));
        yield put({
            type: types.VOUCHER_FAILURE,
            message: error.data.message,
        });
    }
}
